import { GameCategory, MarketOutcome, PeriodType } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	extractNumber,
	extractPlayers,
	extractSelection,
	getMarketSides,
	getPairPlayers,
	getPlayerResult,
	resolveCondition,
	resolveGamePlayerProp,
	resolveMoneyline,
	resolveOvertimeGeneric,
	resolveSpread,
	resolveTeamTotal,
	resolveTotalPoints,
	resolveTotalPointsOddEven,
} from '../../utils';

import {
	buildRaceToQuestion,
	buildSpreadQuestion,
	buildTeamTotalQuestion,
	buildTotalPointsOddEvenQuestion,
	buildTotalPointsQuestion,
	getPeriodScores,
	isFirst3MinutesCompleted,
	resolveRaceTo,
} from '../../utils/market/basketball.utils';

export const basketballConfig: Record<string, MarketConfig> = {
	// Match Outcome
	Moneyline: {
		buildQuestion: (m) => `Will ${m.selection} win the game?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Game),
	},

	// Point Spread (Game or Period)
	'Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Half1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Half2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'1st Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period3),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Point Spread': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period4),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// Game / Period Totals
	'Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Half2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'1st Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Total Points': {
		buildQuestion: (m) => buildTotalPointsQuestion(m.points, PeriodType.Period4),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// Odd / Even Totals
	'Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Game),
		isFinalRequired: true,
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Game),
	},
	'1st Quarter Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Period1),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period1),
	},
	'2nd Quarter Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Period2),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period2),
	},
	'3rd Quarter Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Period3),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period3),
	},
	'4th Quarter Total Points Odd/Even': {
		buildQuestion: () => buildTotalPointsOddEvenQuestion(PeriodType.Period4),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period4),
	},

	// Team Totals
	'Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'2nd Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Half2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Half2),
	},
	'1st Quarter Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Quarter Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Quarter Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Quarter Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period4),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period4),
	},

	// Race to X Points
	'1st Quarter Race To 10 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 10, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 10),
	},
	'1st Quarter Race To 15 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 15, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 15),
	},
	'1st Quarter Race To 20 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 20, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 20),
	},
	'1st Quarter Race To 25 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 25, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 25),
	},
	'1st Quarter Race To 30 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 30, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 30),
	},
	'1st Quarter Race To 35 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 35, PeriodType.Period1),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period1, 35),
	},
	'2nd Quarter Race To 10 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 10, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 10),
	},
	'2nd Quarter Race To 15 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 15, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 15),
	},
	'2nd Quarter Race To 20 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 20, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 20),
	},
	'2nd Quarter Race To 25 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 25, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 25),
	},
	'2nd Quarter Race To 30 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 30, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 30),
	},
	'2nd Quarter Race To 35 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 35, PeriodType.Period2),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period2, 35),
	},
	'3rd Quarter Race To 10 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 10, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 10),
	},
	'3rd Quarter Race To 15 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 15, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 15),
	},
	'3rd Quarter Race To 20 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 20, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 20),
	},
	'3rd Quarter Race To 25 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 25, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 25),
	},
	'3rd Quarter Race To 30 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 30, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 30),
	},
	'3rd Quarter Race To 35 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 35, PeriodType.Period3),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period3, 35),
	},
	'4th Quarter Race To 10 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 10, PeriodType.Period4),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period4, 10),
	},
	'4th Quarter Race To 20 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 20, PeriodType.Period4),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period4, 20),
	},
	'4th Quarter Race To 25 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 25, PeriodType.Period4),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period4, 25),
	},
	'4th Quarter Race To 30 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 30, PeriodType.Period4),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period4, 30),
	},
	'4th Quarter Race To 35 Points': {
		buildQuestion: (m) => buildRaceToQuestion(m.selection, 35, PeriodType.Period4),
		resolve: (q, gc) => resolveRaceTo(q, gc, PeriodType.Period4, 35),
	},
	// Special Game Events
	'Will There Be Overtime': {
		buildQuestion: () => `Will the game go to overtime?`,
		resolve: (_q, gc) => {
			const [lastRegPeriod, firstOTPeriod] =
				gc.category === GameCategory.NBA
					? [PeriodType.Period4, PeriodType.Period5]
					: [PeriodType.Period2, PeriodType.Period3];

			return resolveOvertimeGeneric(gc, lastRegPeriod, firstOTPeriod);
		},
	},
	'First Team To Score': {
		buildQuestion: (m) => `Will ${m.selection} score first?`,
		resolve: (q, _gc, r) => {
			const teamName = extractSelection(q, 'Will ', ['score first?']);

			const firstTeamBasketPlayer = r?.player_results?.find((p: any) =>
				p.stats?.some((s: any) => s.stats?.first_team_basket === 1)
			);

			if (!firstTeamBasketPlayer) {
				return [MarketOutcome.Pending];
			}

			return [
				teamName === firstTeamBasketPlayer.team?.name ? MarketOutcome.Won : MarketOutcome.Lost,
			];
		},
	},

	// Player props
	'Player Points': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} points?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_points', ['score at least']),
	},
	'1st Quarter Player Points': {
		buildQuestion: (m) =>
			`Will ${m.selection} score at least ${m.points} points in the 1st quarter?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['score at least']);
			const playerResult = getPlayerResult(playerName, r);
			const points = playerResult?.player_points ?? 0;
			const isCompleted = gc.isScopeCompleted(PeriodType.Period1);

			return [resolveCondition(points >= threshold, isCompleted), points];
		},
	},
	'1st 3 Minutes Player Points': {
		buildQuestion: (m) =>
			`Will ${m.selection} score at least ${m.points} points in the first 3 minutes?`,
		hasProgressBar: true,
		resolve: (q, _gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['score at least']);
			const playerResult = getPlayerResult(playerName, r);
			const points = playerResult?.player_points ?? 0;
			const isCompleted = isFirst3MinutesCompleted(r);

			return [resolveCondition(points >= threshold, isCompleted), points];
		},
	},
	'Player Rebounds': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} rebounds?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_rebounds', ['record at least']),
	},
	'Player Assists': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} assists?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_assists', ['record at least']),
	},
	'Player Made Threes': {
		buildQuestion: (m) => `Will ${m.selection} make at least ${m.points} three-pointers?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_made_threes', ['make at least']),
	},
	'Player Blocks': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} blocks?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_blocks', ['record at least']),
	},
	'Player Steals': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} steals?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'player_steals', ['record at least']),
	},

	// Combo player stats
	'Player Points + Assists': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} points + assists combined?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_points_+_assists', ['total at least']),
	},
	'Player Points + Rebounds': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} points + rebounds combined?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_points_+_rebounds', ['total at least']),
	},
	'Player Rebounds + Assists': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} rebounds + assists combined?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_rebounds_+_assists', ['total at least']),
	},
	'Player Points + Rebounds + Assists': {
		buildQuestion: (m) =>
			`Will ${m.selection} total at least ${m.points} points + rebounds + assists combined?`,
		hasProgressBar: true,
		resolve: (q, gc, r) =>
			resolveGamePlayerProp(q, gc, r, 'player_points_+_rebounds_+_assists', ['total at least']),
	},
	'Player Points (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);
			return `Will ${playerA} and ${playerB} combine for at least ${m.points} points?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const [playerA, playerB] = extractPlayers(q);
			const total =
				(getPlayerResult(playerA, r)?.player_points ?? 0) +
				(getPlayerResult(playerB, r)?.player_points ?? 0);

			return [resolveCondition(total >= threshold, gc.isCompleted), total];
		},
	},
	// Achievements
	'Player Double Double': {
		buildQuestion: (m) => `Will ${m.selection} record a double-double?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['record a double-double']);
			const playerResult = getPlayerResult(playerName, r);
			const points = playerResult?.player_points ?? 0;
			const rebounds = playerResult?.player_rebounds ?? 0;
			const assists = playerResult?.player_assists ?? 0;
			const categories = [points, rebounds, assists].filter((x) => x >= 10).length;

			return [resolveCondition(categories >= 2, gc.isCompleted)];
		},
	},
	'Player Triple Double': {
		buildQuestion: (m) => `Will ${m.selection} record a triple-double?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['record a triple-double']);
			const playerResult = getPlayerResult(playerName, r);
			const points = playerResult?.player_points ?? 0;
			const rebounds = playerResult?.player_rebounds ?? 0;
			const assists = playerResult?.player_assists ?? 0;
			const categories = [points, rebounds, assists].filter((x) => x >= 10).length;

			return [resolveCondition(categories >= 3, gc.isCompleted)];
		},
	},
	// Head-to-Head
	'Player Points H2H Moneyline': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getMarketSides(m);

			return `Will ${playerA} record more points than ${playerB}?`;
		},
		isFinalRequired: true,
		resolve: (q, _gc, r) => {
			const match = q.match(/^Will (.+) record more points than (.+)\?$/);

			if (!match) {
				throw new Error(`Cannot extract players from question: ${q}`);
			}

			const [, playerA, playerB] = match;

			const playerAPoints = getPlayerResult(playerA, r)?.player_points ?? 0;
			const playerBPoints = getPlayerResult(playerB, r)?.player_points ?? 0;

			return [playerAPoints > playerBPoints ? MarketOutcome.Won : MarketOutcome.Lost];
		},
	},
};
